import React, { Component, createContext, useContext } from 'react'
import uniqueId from "lodash.uniqueid"
import styles from './Area.module.css'

import gearWhite from '../images/gear-white.svg'
import gearBlue from '../images/gear-blue.svg'
import insertIcon from '../images/plus.svg'
import infoIcon from '../images/info.svg'
import deleteIcon from '../images/cross.svg'

import PublishUtils from '../helpers/PublishUtils'
import { Dialog, DialogContent, DialogHeader } from './Dialog'
import { Grow } from '@mui/material'
import { isUserActionsDisabled } from "../redux/current-viewer"
import { WidgetWrapper } from "./WidgetWrapper"
import { LargeTooltip } from "./mui/LargeTooltip"
import { interpolateSubscriptions, WidgetIdContext } from '../helpers/widget'
import { store, useDispatch, useSelector } from '../store'
import { WidgetConfiguration, shouldApplyBorderRadius } from './WidgetConfiguration'
import { allWidgetDefinitionsSorted } from './widgets'
import { postprocessWidgetConfig } from './postprocess-widget-config'
import { WidgetErrorBoundary } from './WidgetErrorBoundary'
import { produce } from 'immer'
import { Signal } from '../utils/signals'
import { WidgetQueryWrapper } from './WidgetQueryWrapper'
import { mergeClasses } from '../helpers/styling'
import { determineWidgetPath } from './editor/widget-tree'
import { useConfigMode } from '@/components/editor/configuration-mode'
import { constructPossiblyUninitializedWidgetPath } from '@/components/editor/widget-selector/widget-selector'
import { useViewerCtx } from "@/hooks/use-app-ctx"
import { useExperimentalConfiguratorUx } from '@/components/editor/experimental-ux'

class AreaComponent extends Component {
  state = {
    dialogVisible: false,
  }

  configChangeSignal = new Signal()

  constructor(props) {
    super(props)
    try
    {
      this.widgetId = uniqueId(`${props.config.widget}-`)
    }
    catch(e){}
  }

  componentWillUnmount() {
    // Make sure to revoke the vote when unmounting
    if (this.hasVotedForUserActionsDisabled()) {
      this.handleVoteForUserActionsDisabled(false)
    }
  }

  onInsertLayout = (name) => {
    let config = {
      ...this.props.config,
    }
    var split = null
    if (
      window.confirm(
        'Do you want this widget to be the first element in the new splitlayout'
      )
    ) {
      split = {
        areaSizes: ['auto', 'auto'],
        areas: [config, {}],
        direction: 'column',
        widget: 'SplitLayout',
      }
    } else {
      if (
        window.confirm(
          'Do you want this widget to be the second element in the new splitlayout'
        )
      ) {
        split = {
          areaSizes: ['auto', 'auto'],
          areas: [{}, config],
          direction: 'column',
          widget: 'SplitLayout',
        }
      } else {
        if (
          window.confirm(
            'Do you want this widget to be the third element in the new splitlayout or cancel all'
          )
        ) {
          split = {
            areaSizes: ['auto', 'auto', 'auto'],
            areas: [{}, {}, config],
            direction: 'column',
            widget: 'SplitLayout',
          }
        } else {
          return
        }
      }
    }

    this.setConfig(split, this.props.index)
  }

  
  onDeleteWidget = (name) => {
    if (window.confirm('are you sure that you want to delete this?')) {
      let config = {
        ...this.props.config,
      }

      config = {}
      this.setConfig(config, this.props.index)
    }
  }

  onChildChange = (config, index) => {
    let newConfig = produce(this.props.config, draft => {
      draft.areas[index] = config
    })
    try {
      this.setConfig(newConfig, this.props.index)
    } catch(e) {
      console.error(e)
    }
  }

  setConfig = (config, index) => {
    this.props.onChange(config, index)
    this.configChangeSignal.emit(config)
  }

  onShowPopupInfo = () => {
    this.setState({ popupInfoVisible: true })
  }

  infoPopupClose = () => {
    this.setState({ popupInfoVisible: false })
  }

  openConfigurationWindow = () => this.setState({dialogVisible: true})
  closeConfigurationWindow = () => this.setState({dialogVisible: false})

  /**
   * Not subscribing to this value from the store, for performance reasons.
   * In the conventional manner we would use mapStateToProps, but that triggers unnecessary cascading rerenders.
   * @return {boolean}
   */
  hasVotedForUserActionsDisabled() {
    return store.getState().currentViewer.votersForGlobalUserActionsDisabled.has(this.widgetId)
  }

  /**
   * @param userActionsDisabled {boolean}
   */
  handleVoteForUserActionsDisabled = (userActionsDisabled) => {
    this.props.dispatch({
      type: "VOTE_USER_ACTIONS_DISABLED",
      data: {
        voterId: this.widgetId,
        value: userActionsDisabled
      }
    })
  }

  renderWidget(config, widgetDefinition) {
    if (!config.widget) {
      if (this.props.mode === 'edit') return <div>Selecteer een widget.</div>
      return
    }
    if ((this.props.mode === 'edit') && (widgetDefinition==null)) return <div>Selecteer een widget.</div>
    if (widgetDefinition==null) return null;
    if (interpolateSubscriptions(this, config.hidden, 'boolean')) return null

    const widgetUserActionsDisabled = !!config.syncWithUserActionsDisabled && this.props.userActionsDisabled

    return (
      <WidgetIdContext.Provider value={this.widgetId}>
        <WidgetWrapper
          userActionsDisabled={widgetUserActionsDisabled}
          clickable={!!this.props.onClick}
          onClick={this.props.onClick}
          style={{
            fontFamily: config.fontFamily ?? undefined
          }}
        >
          <WidgetQueryWrapper
            reauth={this.props.reauth}
            widgetId={this.widgetId}
            repoName={this.props.repoName}
            repoURL={this.props.repoURL}
            parameterValues={this.props.parameterValues}
            localRepoURL={this.props.localRepoURL}
            mode={this.props.mode}
            save={this.props.save}
            sparqlErrorMessage={this.props.sparqlErrorMessage}
            withinSelectedTab={this.props.withinSelectedTab}
            parseUrlProperties={this.props.parseUrlProperties}
            {...postprocessWidgetConfig(config, widgetDefinition, this)}
            definition={widgetDefinition}
            onChange={this.onChildChange}
            closeDialog={this.props.closeDialog}
            userActionsDisabled={widgetUserActionsDisabled}
            voteForUserActionsDisabled={this.handleVoteForUserActionsDisabled}
            notEditable={this.props.notEditable}
            namespace={this.props.namespace}
            configMode={this.props.configMode}
          >
            <widgetDefinition.component />
          </WidgetQueryWrapper>
        </WidgetWrapper>
      </WidgetIdContext.Provider>
    )
  }

  /**
   * @param config {WidgetConfig}
   * @param widgetDefinition {WidgetDescription<any, any, any>}
   * @return {Record<string, string>}
   */
  widgetPathAttributes(config, widgetDefinition) {
    const { isExperimentalUx, parentConfig, viewerCtx, index, notEditable } = this.props
    if (!isExperimentalUx) return {}
    if (notEditable || viewerCtx?.host !== "viewer") return {}

    const parentWidgetPath = parentConfig
      ? determineWidgetPath(parentConfig, viewerCtx.mainArea)
      : null

    return {
      "data-widget-id": this.widgetId,
      "data-parent-widget-path": parentWidgetPath,
      "data-widget-path": constructPossiblyUninitializedWidgetPath({
        widgetPath: determineWidgetPath(config, viewerCtx.mainArea),
        parentWidgetPath,
        areaIndex: index
      }),
      "data-layout-widget": widgetDefinition?.hasChildAreas ? "true" : "false",
    }
  }

  render() {
   // if (this.props.withinSelectedTab==false) return false;

  // console.log(this);
    let config = this.props.config || {}
   




    let menubar = false
    let titleMenuBar = config.titleMenubar
    if (titleMenuBar != null) {
      titleMenuBar = titleMenuBar.trim()
      titleMenuBar = PublishUtils.processStringForParameters(this, titleMenuBar)
      titleMenuBar=PublishUtils.removeUnresolvedParametersFromString(titleMenuBar)

    }
    if (titleMenuBar != null && titleMenuBar !== '' && titleMenuBar !== ' ') {
      menubar = true
    }
    //  console.log("titleMenubar '"+titleMenuBar+"'");

    let mode = this.props.mode
    const widgetDefinition = allWidgetDefinitionsSorted.find(w => w.name === config.widget)

    let extraStyling1 = ''
    let extraStyling2 = ''
    let extraStyling3 = ''
    let extraStyling4 = ''
    if (config.panelstyleExtra != null) {
      var sts = config.panelstyleExtra.split(' ')
      try {
        extraStyling1 = sts[0]
      } catch (e) {}
      try {
        extraStyling2 = sts[1]
      } catch (e) {}
      try {
        extraStyling3 = sts[2]
      } catch (e) {}
      try {
        extraStyling4 = sts[3]
      } catch (e) {}
    }
    let displayFlex = ' ' + styles.outerAreaContainerDisplayFlex
    //not used
    if (this.props.disableFlexbox) {
      //  console.log("disable flexbox for this area")  ;
      displayFlex = ''
    }
    let infoMenubarb = false
    if (config.infoMenubar != null) {
      if (config.infoMenubar.length > 2) {
        infoMenubarb = true
      }
    }
    var hidden = config.hidden;
    if (hidden==null) hidden=false;
    if (hidden!=false) {
    
      hidden=PublishUtils.processStringForParameters(this,hidden);
      try {hidden=JSON.parse(hidden.toLowerCase());} catch(e){}
      //console.log("2",hidden,mode);
      //if (hidden==true) console.log("3 hidden=true");
      //if (hidden==false) console.log("4 hidden=false");
     
    }
    var hidden2= config.hiddenwhenparametersareempty;
    if (hidden2?.length)
    {
      
      var hids2=hidden2.split(",");
    //console.log("hidden2",hids2);
      for (var n in hids2)
      {
        var hid2=hids2[n];
         if (this.props.pubsub[hid2]==null) hidden=true;
         if (this.props.pubsub[hid2]=="http://www.buildingbits.nl/reset") hidden=true;
      }
    }

    const editModeIsEnabled = !this.props?.notEditable && mode === 'edit'

    var visibilityState =hidden ? "hidden" : "visible";
    if (editModeIsEnabled)  {  visibilityState="visible"; }

    return (
     <AreaTransition enabled={!this.props.isExperimentalUx}>
        <div
          className={
            styles.outerAreaContainer +
            ' ' +
            (shouldApplyBorderRadius(config.widget) ? '' : styles.zeroBorderRadiusWidget) +
            ' ' +
            displayFlex +
            ' ' +
            (editModeIsEnabled ? '' : styles.viewMode) +
            ' ' +
            styles[config.widget] +
            ' ' +
            styles[config.panelstyle] +
            ' ' +
            styles[extraStyling1] +
            ' ' +
            styles[extraStyling2] +
            ' ' +
            styles[extraStyling3] +
            ' ' +
            styles[extraStyling4]
          }
          style={{
            visibility: visibilityState,
            '--area-border-style': config.areaBorderStyle,
            '--area-border-radius': config.areaBorderRadius,
            '--final-top-left-border-radius': config.finalTopLeftBorderRadius,
            '--final-top-right-border-radius': config.finalTopRightBorderRadius,
            '--final-bottom-left-border-radius': config.finalBottomLeftBorderRadius,
            '--final-bottom-right-border-radius': config.finalBottomRightBorderRadius,
          }}
          data-custom-styling={Boolean(config.customStyling)}
          data-final-top-left-border-radius={!!config.finalTopLeftBorderRadius}
          data-final-top-right-border-radius={!!config.finalTopRightBorderRadius}
          data-final-bottom-left-border-radius={!!config.finalBottomLeftBorderRadius}
          data-final-bottom-right-border-radius={!!config.finalBottomRightBorderRadius}
          data-widget-type={config.widget}
          {...this.widgetPathAttributes(config, widgetDefinition)}
        >
          {editModeIsEnabled && (
            <div className={styles.hoverToEdit}>
              <div className={styles.hoverIndicator} aria-label="Hover to reveal widget toolbar" />
              <LargeTooltip
                placement="bottom"
                title={config.widget && `${config.widget} widget`}
              >
                <div className={styles.editArea} role="toolbar" aria-label="Widget toolbar">
                  <img
                    src={insertIcon}
                    alt="box"
                    className={styles.insertIcon}
                    aria-label="Insert widget"
                    onClick={() => this.onInsertLayout(config.widget)}
                  />
                  <img
                    src={'areas' in config ? gearBlue : gearWhite}
                    alt="gear"
                    className={styles.editIcon}
                    aria-label="Configure widget"
                    onClick={this.openConfigurationWindow}
                  />
                  <img
                    src={deleteIcon}
                    alt="cross"
                    className={styles.insertIcon}
                    aria-label="Delete widget"
                    onClick={() => this.onDeleteWidget(config.widget)}
                  />
                </div>
              </LargeTooltip>
            </div>
          )}

          {menubar && (
            <div className={styles.menubar}>
              <div className={styles.menubartxt}> {titleMenuBar}</div>
              {infoMenubarb && (
                <img
                  src={infoIcon}
                  alt="box"
                  className={styles.insertIcon}
                  onClick={() => this.onShowPopupInfo()}
                />
              )}
            </div>
          )}

          <ParentAreaContext.Provider value={config}>
            <WidgetErrorBoundary
              configSignal={this.configChangeSignal}
              widgetId={this.widgetId}
            >
              {this.renderWidget(config, widgetDefinition)}
            </WidgetErrorBoundary>
          </ParentAreaContext.Provider>

          {(this.props.isExperimentalUx
            && !this.props.notEditable
            && this.props.configMode === "edit"
            && (widgetDefinition?.hasChildAreas || true))
            && (
            <>
              <div className={mergeClasses(styles["layout-widget-select-helper"], 'top')} />
              <div className={mergeClasses(styles["layout-widget-select-helper"], 'bottom')} />
              <div className={mergeClasses(styles["layout-widget-select-helper"], 'left')} />
              <div className={mergeClasses(styles["layout-widget-select-helper"], 'right')} />
            </>
          )}

          {this.state.popupInfoVisible && (
            <Dialog onClose={this.infoPopupClose} frameClasses="max-w-[400px]">
              <DialogHeader />
              <DialogContent className="text-center">
                {config.infoMenubar}
              </DialogContent>
            </Dialog>
          )}

          {this.state.dialogVisible && (
            <WidgetIdContext.Provider value={this.widgetId}>
              <WidgetConfiguration
                areaPath={this.props.areaPath}
                configRendered={config}
                widgetDefinition={widgetDefinition}
                close={this.closeConfigurationWindow}
                saveConfigRendered={this.props.save}
                setConfigToRender={config => this.setConfig(config, this.props.index)}
              />
            </WidgetIdContext.Provider>
          )}
        </div>
        </AreaTransition>

    )
  }
}

/**
 * Where an area sits in the layout, accumulated down the tree: "main",
 * "main.0", "main.0.1".
 *
 * It describes the app config rather than the render, so it is the same on
 * every visit - which is what lets per-field editor history find its way back
 * to the right widget after a reload. The alternative to hand, WidgetIdContext,
 * is a uniqueId() counter that restarts each page load.
 */
const AreaPathContext = createContext('main')

/**
 * Grow-in animation for an area.
 * Optional, because it can desynchronize widget selection outline in new configurator UX.
 */
function AreaTransition({ enabled, children }) {
  if (!enabled) return children
  return <Grow appear timeout={1000} in={true}>{children}</Grow>
}

const ParentAreaContext = createContext(null)

/**
 * Every container that renders an area into a slot already passes `index`, so
 * the path builds itself here. Threading it as a prop instead would make each
 * container responsible for extending it, and a container that forgot would
 * silently hand all of its children one shared path.
 */
export default function Area(props) {
  const parentPath = useContext(AreaPathContext)
  const areaPath = props.index === undefined ? parentPath : `${parentPath}.${props.index}`
  const userActionsDisabled = isUserActionsDisabled(useSelector(st => st.currentViewer))
  const configMode = useConfigMode()
  const parentConfig = useContext(ParentAreaContext)
  const viewerCtx = useViewerCtx()
  const isExperimentalUx = useExperimentalConfiguratorUx()
  const dispatch = useDispatch()
  return (
    <AreaPathContext.Provider value={areaPath}>
      <AreaComponent
        {...props}
        areaPath={areaPath}
        dispatch={dispatch}
        userActionsDisabled={userActionsDisabled}
        configMode={configMode}
        parentConfig={parentConfig}
        viewerCtx={viewerCtx}
        isExperimentalUx={isExperimentalUx}
      />
    </AreaPathContext.Provider>
  )
}
